import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { GET_USER_CAMPAIGNS } from "graphql/campaign/campaign.query";
import { GET_SURVEY_QUESTIONS_BY_SURVEY_ID } from "graphql/surveyQuestion/survey_question.query";
import { GET_ANSWERS_BY_USER_AND_QUESTION_IDS } from "graphql/answer/answer.query";
import { useAuthUser } from "hooks/useAuthUser";
import { Campaign } from "types/campaign";

export const useUnansweredCampaigns = () => {
  const { user } = useAuthUser();

  const [unansweredCampaign, setUnansweredCampaign] = useState<Campaign | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const isCheckingRef = useRef(false);

  const {
    data: userCampaignsData,
    loading: campaignsLoading,
    refetch: refetchCampaigns,
  } = useQuery(GET_USER_CAMPAIGNS, {
    variables: { userId: user?.user_id },
    skip: !user?.user_id,
    fetchPolicy: "cache-first", // Changed from network-only to prevent unnecessary refetches
  });

  const [getSurveyQuestions] = useLazyQuery(GET_SURVEY_QUESTIONS_BY_SURVEY_ID);
  const [getAnswersByUserAndQuestionIds] = useLazyQuery(
    GET_ANSWERS_BY_USER_AND_QUESTION_IDS,
  );

  const userCampaigns = useMemo(() => {
    if (!userCampaignsData?.campaign_user) return [];

    console.log("Processing user campaigns data:", userCampaignsData);

    return userCampaignsData.campaign_user
      .map((cu: any) => cu.campaign)
      .filter((campaign: Campaign | null) => {
        if (!campaign) return false;
        // Check if campaign is active (between start_ts and end_ts)
        const now = new Date();
        const startTs = new Date(campaign.start_ts);
        const endTs = new Date(campaign.end_ts);

        return now >= startTs && now <= endTs;
      });
  }, [userCampaignsData]);

  const checkUnansweredCampaigns = useCallback(async () => {
    console.log("Checking for unanswered campaigns...", userCampaigns);

    if (
      !user?.user_id ||
      campaignsLoading ||
      userCampaigns.length === 0 ||
      isCheckingRef.current
    ) {
      if (!campaignsLoading) {
        setLoading(false);
      }
      return;
    }

    isCheckingRef.current = true;
    setLoading(true);

    try {
      // Helper function to add frequency interval to a date
      const addFrequencyInterval = (date: Date, frequency: string): Date => {
        const result = new Date(date);
        switch (frequency) {
          case "daily":
            result.setDate(result.getDate() + 1);
            break;
          case "weekly":
            result.setDate(result.getDate() + 7);
            break;
          case "monthly":
            result.setMonth(result.getMonth() + 1);
            break;
          case "once":
          default:
            // For "once", we'll handle it separately
            break;
        }
        return result;
      };

      // Check each campaign to see if user has answered all surveys
      for (const campaign of userCampaigns) {
        console.log(
          `Checking campaign: ${campaign.campaign_id} - ${campaign.name}`,
        );
        if (
          !campaign.campaign_id ||
          !campaign.surveys ||
          campaign.surveys.length === 0
        ) {
          continue;
        }

        // For "once" frequency, check if there are any answers at all
        if (campaign.frequency === "once") {
          let hasAnyAnswers = false;
          let allQuestionsAnswered = true;

          // Check each survey in the campaign
          for (const campaignSurvey of campaign.surveys) {
            const surveyId = campaignSurvey.survey_id;
            if (!surveyId) continue;

            try {
              // Get all questions for this survey
              const { data: surveyQuestionsData } = await getSurveyQuestions({
                variables: { surveyId },
              });

              if (
                !surveyQuestionsData?.survey_question ||
                surveyQuestionsData.survey_question.length === 0
              ) {
                continue;
              }

              const questionIds = surveyQuestionsData.survey_question
                .map((sq: any) => sq.question_id)
                .filter(Boolean);

              if (questionIds.length === 0) {
                continue;
              }

              // Get answers by user for these questions
              const { data: answersData } =
                await getAnswersByUserAndQuestionIds({
                  variables: {
                    userId: user.user_id,
                    questionIds,
                  },
                });

              const answers = answersData?.answer || [];
              if (answers.length > 0) {
                hasAnyAnswers = true;
              }

              // Check if user has answered all questions
              const answeredQuestionIds = new Set(
                answers.map((ans: any) => ans.question_id),
              );

              const surveyAllQuestionsAnswered = questionIds.every(
                (qId: string) => answeredQuestionIds.has(qId),
              );

              if (!surveyAllQuestionsAnswered) {
                allQuestionsAnswered = false;
                break;
              }
            } catch (error) {
              console.error(`Error checking survey ${surveyId}:`, error);
              allQuestionsAnswered = false;
              break;
            }
          }

          // For "once" frequency, if there are any answers, don't show again
          if (hasAnyAnswers && allQuestionsAnswered) {
            continue; // Skip this campaign, user already answered
          }

          // If not all questions answered, show the campaign
          if (!allQuestionsAnswered) {
            setUnansweredCampaign(campaign);
            setLoading(false);
            isCheckingRef.current = false;
            return;
          }
        } else {
          // For other frequencies (daily, weekly, monthly)
          let allAnswerDates: Date[] = [];
          let hasUnansweredSurvey = false;

          // Check each survey in the campaign
          for (const campaignSurvey of campaign.surveys) {
            const surveyId = campaignSurvey.survey_id;
            if (!surveyId) continue;

            try {
              // Get all questions for this survey
              const { data: surveyQuestionsData } = await getSurveyQuestions({
                variables: { surveyId },
              });

              if (
                !surveyQuestionsData?.survey_question ||
                surveyQuestionsData.survey_question.length === 0
              ) {
                continue;
              }

              const questionIds = surveyQuestionsData.survey_question
                .map((sq: any) => sq.question_id)
                .filter(Boolean);

              if (questionIds.length === 0) {
                continue;
              }

              // Get answers by user for these questions
              const { data: answersData } =
                await getAnswersByUserAndQuestionIds({
                  variables: {
                    userId: user.user_id,
                    questionIds,
                  },
                });

              const answers = answersData?.answer || [];

              // Check if user has answered all questions
              const answeredQuestionIds = new Set(
                answers.map((ans: any) => ans.question_id),
              );

              const allQuestionsAnswered = questionIds.every((qId: string) =>
                answeredQuestionIds.has(qId),
              );

              if (!allQuestionsAnswered) {
                hasUnansweredSurvey = true;
                break;
              }

              // Collect all answer dates
              answers.forEach((ans: any) => {
                if (ans.created_at) {
                  allAnswerDates.push(new Date(ans.created_at));
                }
              });
            } catch (error) {
              console.error(`Error checking survey ${surveyId}:`, error);
              hasUnansweredSurvey = true;
              break;
            }
          }

          // If there are unanswered surveys, show the campaign
          if (hasUnansweredSurvey) {
            setUnansweredCampaign(campaign);
            setLoading(false);
            isCheckingRef.current = false;
            return;
          }

          // If all surveys are answered, check the frequency interval
          if (allAnswerDates.length > 0) {
            // Find the most recent answer date
            const mostRecentAnswerDate = new Date(
              Math.max(...allAnswerDates.map((d) => d.getTime())),
            );

            // Add frequency interval
            const nextShowDate = addFrequencyInterval(
              mostRecentAnswerDate,
              campaign.frequency,
            );

            // Only show if nextShowDate is in the past (or now)
            const now = new Date();
            if (nextShowDate <= now) {
              setUnansweredCampaign(campaign);
              setLoading(false);
              isCheckingRef.current = false;
              return;
            }
          } else {
            // No answers at all, show the campaign
            setUnansweredCampaign(campaign);
            setLoading(false);
            isCheckingRef.current = false;
            return;
          }
        }
      }

      // No unanswered campaigns found
      setUnansweredCampaign(null);
      setLoading(false);
      isCheckingRef.current = false;
    } catch (error) {
      console.error("Error checking unanswered campaigns:", error);
      setLoading(false);
      isCheckingRef.current = false;
    }
  }, [
    user?.user_id,
    campaignsLoading,
    userCampaigns,
    getSurveyQuestions,
    getAnswersByUserAndQuestionIds,
  ]);

  useEffect(() => {
    checkUnansweredCampaigns();
  }, [checkUnansweredCampaigns]);

  return { unansweredCampaign, loading, refetchCampaigns };
};
